import { EntitlementKey } from './registry';

/** How a customer sees what they may request. */
export enum RequestCatalogueGroup {
  PACKAGE = 'package',
  LENS = 'lens',
  FEATURE = 'feature',
  QUOTA = 'quota',
}

export interface RequestCatalogueEntry {
  key: EntitlementKey;
  // The name a customer sees.
  name: string;
  group: RequestCatalogueGroup;
}

/**
 * What a customer may request for their organisation: the keys whose checks read the commercial
 * record for an organisation on it, and so the keys a request can be fulfilled with. Ops fulfils
 * every entry the same way, by adding one organisation-wide agreement item of its key ("Provision
 * agreement" or "Add items"); a quota item carries the limit Ops chooses, above the allowance in
 * effect. Features that come with every contract (search bar, Excel and image downloads, editing the
 * current weight set, time tracking) and features gated only until their release are not sold, so
 * they are not here. Rate limits are operational and not here either.
 */
export const requestCatalogue: readonly RequestCatalogueEntry[] = [
  { key: EntitlementKey.COVERAGE_STANDARD, name: 'Standard coverage', group: RequestCatalogueGroup.PACKAGE },
  { key: EntitlementKey.COVERAGE_EXTENDED, name: 'Extended coverage', group: RequestCatalogueGroup.PACKAGE },
  { key: EntitlementKey.NET_IMPACT, name: 'Net impact', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.SDG_ALIGNMENT, name: 'SDG alignment', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.EU_TAXONOMY, name: 'EU taxonomy', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.EU_PAI, name: 'EU PAI', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.API, name: 'API', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.IMPACT_EXPORT, name: 'Impact data export', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.EDIT_ALL_WEIGHT_SETS, name: 'Edit all weight sets', group: RequestCatalogueGroup.FEATURE },
  {
    key: EntitlementKey.CSRD_GROUPFOLIO_CONSTITUENT_EXPORT,
    name: 'CSRD constituent export',
    group: RequestCatalogueGroup.FEATURE,
  },
  { key: EntitlementKey.PRODUCT_VIEW, name: 'Product view', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.PRODUCT_SEARCH, name: 'Product search', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.AI_CHAT, name: 'AI chat', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.AI_CONNECTOR, name: 'AI connector', group: RequestCatalogueGroup.FEATURE },
  { key: EntitlementKey.CHAT_TOKENS, name: 'AI chat usage', group: RequestCatalogueGroup.QUOTA },
  { key: EntitlementKey.MCP_CONNECTOR_TOOL_CALLS, name: 'AI connector tool calls', group: RequestCatalogueGroup.QUOTA },
];

/**
 * Assessments requested for an exact company and scenario. A plan item fulfils the request;
 * readiness follows that item's live window, just like organisation-wide plan items.
 */
export const lensRequestCatalogue: readonly RequestCatalogueEntry[] = [
  { key: EntitlementKey.TAILORING, name: 'Double materiality', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.FE, name: 'Financial effects', group: RequestCatalogueGroup.LENS },
  { key: EntitlementKey.CLIMATE, name: 'Climate', group: RequestCatalogueGroup.LENS },
];

const entriesByKey: ReadonlyMap<string, RequestCatalogueEntry> = new Map(requestCatalogue.map(e => [e.key, e]));

const lensEntriesByKey: ReadonlyMap<string, RequestCatalogueEntry> = new Map(lensRequestCatalogue.map(e => [e.key, e]));

/** The catalogue entry of a key, or undefined when a customer may not request it. */
export function findRequestCatalogueEntry(key: string): RequestCatalogueEntry | undefined {
  return entriesByKey.get(key);
}

/** The company-scoped lens entry of a key, or undefined when it is not a lens a customer may request. */
export function findLensRequestCatalogueEntry(key: string): RequestCatalogueEntry | undefined {
  return lensEntriesByKey.get(key);
}
